// src/components/workflow/FieldConfigModal.jsx
import { useEffect, useState } from "react";
import CustomSelect from "../common/CustomSelect";
import { handleCustomResponse } from "../../utils/response";

// Field Configuration Modal
export const FieldConfigModal = ({
  isOpen,
  onClose,
  field,
  onSave,
  isEditMode,
  fieldNames,
  optionSources,
}) => {
  const [config, setConfig] = useState({
    name: "",
    label: "",
    required: false,
    validation: "",
    min: "",
    max: "",
    type: "",
    options: "",
    options_source: null,
    options_label_field: null,
    options_value_field: null,
    order: 1,
  });
  const [optionSourceType, setOptionSourceType] = useState("");
  const [selectedApiSource, setSelectedApiSource] = useState(null); // Changed to null for consistency
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  useEffect(() => {
    if (field) {
      let min = "";
      let max = "";
      if (
        ["text", "number", "textarea"].includes(field.type) &&
        field.validation
      ) {
        const validationParts = field.validation.split("|");
        validationParts.forEach((part) => {
          if (part.startsWith("min:")) min = part.split(":")[1];
          if (part.startsWith("max:")) max = part.split(":")[1];
        });
      }
      setConfig({
        name: field.name || "",
        label: field.label || "",
        required: field.required || false,
        validation: field.validation || "",
        min: min,
        max: max,
        type: field.type || "",
        options: Array.isArray(field.options)
          ? field.options.join("|")
          : field.options || "",
        options_source: field.options_source || null,
        options_label_field: field.options_label_field || null,
        options_value_field: field.options_value_field || null,
        order: field.order || 1,
      });
      setOptionSourceType(
        field.options_source
          ? "api"
          : field.options && field.options.length > 0
          ? "hard_coded"
          : ""
      );
      // Find the full API source object from optionSources.api
      const apiSource = field.options_source
        ? optionSources.api.find(
            (source) => source.url === field.options_source
          )
        : null;
      setSelectedApiSource(apiSource || null);
    }
  }, [field, optionSources]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSourceTypeChange = (selectedOption) => {
    const type = selectedOption?.value;
    setOptionSourceType(type);
    setConfig((prev) => ({
      ...prev,
      options: "",
      options_source: null,
      options_label_field: null,
      options_value_field: null,
    }));
    setSelectedApiSource(null); // Clear selected API source
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!config.label.trim()) {
      handleCustomResponse("error", "Field Label cannot be empty.");
      return;
    }
    if (!config.name) {
      handleCustomResponse("error", "Please select a Field Name.");
      return;
    }
    if (config.type === "select") {
      if (
        optionSourceType === "hard_coded" &&
        (!config.options || config.options.trim() === "")
      ) {
        handleCustomResponse(
          "error",
          "Please add at least one option for hardcoded dropdown."
        );
        return;
      }
      if (optionSourceType === "api" && !selectedApiSource) {
        handleCustomResponse(
          "error",
          "Please select an API source for the dropdown."
        );
        return;
      }
    }
    if (["text", "number", "textarea"].includes(config.type)) {
      if (config.min && isNaN(config.min)) {
        handleCustomResponse("error", "Minimum value must be a number.");
        return;
      }
      if (config.max && isNaN(config.max)) {
        handleCustomResponse("error", "Maximum value must be a number.");
        return;
      }
      if (
        config.min &&
        config.max &&
        parseFloat(config.min) > parseFloat(config.max)
      ) {
        handleCustomResponse(
          "error",
          "Minimum value cannot be greater than maximum value."
        );
        return;
      }
    }

    const validation = ["text", "number", "textarea"].includes(config.type)
      ? [config.min && `min:${config.min}`, config.max && `max:${config.max}`]
          .filter(Boolean)
          .join("|")
      : config.validation;

    const fieldToSave = {
      ...field,
      ...config,
      validation,
      options:
        config.type === "select" &&
        optionSourceType === "hard_coded" &&
        config.options
          ? config.options.split("|").map((opt) => opt.trim())
          : null,
      options_source:
        optionSourceType === "api" ? selectedApiSource?.url : null,
      options_label_field:
        optionSourceType === "api" ? selectedApiSource?.label_field : null,
      options_value_field:
        optionSourceType === "api" ? selectedApiSource?.value_field : null,
    };

    onSave(fieldToSave);
    onClose();
  };

  if (!isOpen) return null;

  const fieldNameOptions = fieldNames.map((field) => ({
    value: field.field_name,
    label: field.field_name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));

  const sourceTypeOptions = [
    { value: "hard_coded", label: "Write By Own" },
    { value: "api", label: "Get By API" },
  ];

  const apiSourceOptions = optionSources.api.map((source) => ({
    value: source.url, // Use URL as the value for simplicity
    label: source.label,
  }));

  // const showValidationInputs = ["text", "number", "textarea"].includes(
  //   config.type
  // );
  const showValidationInputs = ["no-text"].includes(
    config.type
  );

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ width: "450px" }}
      >
        <h3 className="mb-24">{isEditMode ? "Edit Field" : "Add Field"}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Name</label>
            <CustomSelect
              options={fieldNameOptions}
              value={fieldNameOptions.find((opt) => opt.value === config.name)}
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: "name",
                    value: selectedOption?.value || "",
                  },
                });
                handleChange({
                  target: {
                    name: "label",
                    value: selectedOption?.label || "",
                  },
                });
              }}
              placeholder="-- Select Field Name --"
              isSearchable
            />
          </div>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Label</label>
            <input
              type="text"
              className="form-control"
              name="label"
              value={config.label}
              onChange={handleChange}
              required
            />
          </div>
          {showValidationInputs && (
            <div className="mb-16">
              <label className="form-label fw-bold">Validation</label>
              <div className="row">
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="min"
                    min={0}
                    value={config.min}
                    onChange={handleChange}
                    placeholder="Min Length/Value"
                  />
                </div>
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="max"
                    value={config.max}
                    onChange={handleChange}
                    placeholder="Max Length/Value"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mb-16 form-check d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              id="required-checkbox"
              name="required"
              checked={config.required}
              onChange={handleChange}
            />
            <label
              htmlFor="required-checkbox"
              className="form-check-label ms-2"
            >
              Required
            </label>
          </div>
          {config.type === "select" && (
            <div className="mb-16">
              <label className="form-label fw-bold">Options Source</label>
              <CustomSelect
                options={sourceTypeOptions}
                value={sourceTypeOptions.find(
                  (opt) => opt.value === optionSourceType
                )}
                onChange={handleSourceTypeChange}
                placeholder="-- Select Source Type --"
              />
              {optionSourceType === "hard_coded" && (
                <div className="mt-16">
                  <label className="form-label fw-bold">
                    Dropdown Options (pipe-separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="options"
                    value={config.options}
                    onChange={handleChange}
                    placeholder="e.g., option1|option2|option3"
                  />
                </div>
              )}
              {optionSourceType === "api" && (
                <div
                  className="mt-16"
                  style={{ marginBottom: isSelectOpen ? "250px" : "" }}
                >
                  <label className="form-label fw-bold">
                    Select API Source
                  </label>
                  <CustomSelect
                    options={apiSourceOptions}
                    value={
                      apiSourceOptions.find(
                        (opt) => opt.value === selectedApiSource?.url
                      ) || null
                    }
                    onChange={(selected) => {
                      setSelectedApiSource(
                        selected
                          ? optionSources.api.find(
                              (source) => source.url === selected.value
                            )
                          : null
                      );
                    }}
                    placeholder="-- Select API --"
                    isClearable
                    onMenuOpen={() => setIsSelectOpen(true)} // ✅ Yahan use karo
                    onMenuClose={() => setIsSelectOpen(false)} // ✅ Yahan use karo
                  />
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              {isEditMode ? "Update Field" : "Add Field"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};